import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dispatch } from 'redux';

import { requestHandler } from '../../api';
import {
  CreateLegalCollectionsInput,
  GetAvailableDocumentsResponse,
  GetLegalCollectionsResponse,
  UpdateLegalCollectionInput,
} from './types';

export const getLegalCollections = (dispatch: Dispatch) =>
  useQuery({
    queryKey: ['legalCollections'],
    queryFn: async (): Promise<GetLegalCollectionsResponse | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: '/internal/legalCollection/getAll',
        dispatch,
      });
      if (response) return response.data;
    },
    retry: 1,
  });

export const getAvailableDocuments = (dispatch: Dispatch) =>
  useQuery({
    queryKey: ['availableDocumentsForLegalCollection'],
    queryFn: async (): Promise<GetAvailableDocumentsResponse | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: '/internal/legalCollection/availableDocuments',
        dispatch,
      });
      if (response) return response.data;
    },
    retry: 1,
  });

export const createLegalCollections = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['createLegalCollections'],
    mutationFn: async (
      data: CreateLegalCollectionsInput,
    ): Promise<{ message: string } | undefined> => {
      const response = await requestHandler({
        method: 'post',
        url: '/internal/legalCollection/createBatch',
        dispatch,
        data,
      });
      if (response) return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['legalCollections'] });
    },
  });
};

export const updateLegalCollection = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['updateLegalCollection'],
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: UpdateLegalCollectionInput;
    }): Promise<{ message: string } | undefined> => {
      const response = await requestHandler({
        method: 'put',
        url: `/internal/legalCollection/update/${id}`,
        dispatch,
        data,
      });
      if (response) return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['legalCollections'] });
    },
  });
};

export const deleteLegalCollection = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['deleteLegalCollection'],
    mutationFn: async (id: number): Promise<void> => {
      await requestHandler({
        method: 'delete',
        url: `/internal/legalCollection/delete/${id}`,
        dispatch,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['legalCollections'] });
    },
  });
};
